import { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { toast } from "react-toastify";
import { Constants } from "../../../constants/Constants";
import { PathName } from "../../../constants/PathName";
import {
	useCreateFormBuilderFormMutation,
	useGetFormBuilderFormQuery,
	useListFormBuilderItemFieldTypesQuery,
	useListFormBuilderSelectsQuery,
	useUpdateFormBuilderFormMutation,
} from "../../../generated/nuggetKmsApi/graphql";
import { useExportFormResponses } from "./useExportFormResponses";
import { BsFiletypeCsv } from "react-icons/bs";

interface FormBuilderItemFieldState {
	code: string;
	name: string;
	description: string;
	sortOrder: number;
	typeCode: string;
	isRequired: boolean;
	selectId?: number | null;
	placeholder: string;
	subtitle: string;
}

interface FormBuilderItemState {
	code: string;
	name: string;
	description: string;
	sortOrder: number;
	relatedFormCode: string;
	relatedSectionCode: string;
	relatedItemCode: string;
	relatedItemFieldCode: string;
	itemFields: FormBuilderItemFieldState[];
}

interface FormBuilderSectionState {
	code: string;
	name: string;
	description: string;
	sortOrder: number;
	items: FormBuilderItemState[];
}

interface FormBuilderFormState {
	code: string;
	name: string;
	description: string;
	closingMessage: string;
	logoUrl: string;
	sections: FormBuilderSectionState[];
}

const createEmptyField = (sortOrder: number): FormBuilderItemFieldState => ({
	code: "",
	name: "",
	description: "",
	sortOrder,
	typeCode: "",
	isRequired: false,
	selectId: null,
	placeholder: "",
	subtitle: "",
});

const createEmptyItem = (sortOrder: number): FormBuilderItemState => ({
	code: "",
	name: "",
	description: "",
	sortOrder,
	relatedFormCode: "",
	relatedSectionCode: "",
	relatedItemCode: "",
	relatedItemFieldCode: "",
	itemFields: [createEmptyField(1)],
});

const createEmptySection = (sortOrder: number): FormBuilderSectionState => ({
	code: "",
	name: "",
	description: "",
	sortOrder,
	items: [createEmptyItem(1)],
});

const createEmptyForm = (): FormBuilderFormState => ({
	code: "",
	name: "",
	description: "",
	closingMessage: "",
	logoUrl: "",
	sections: [createEmptySection(1)],
});

const toNumber = (value: string) => {
	const parsed = Number(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export const FormBuilderFormEdit = () => {
	const { formId } = useParams<{ formId?: string }>();
	const history = useHistory();
	const [formState, setFormState] = useState<FormBuilderFormState>(createEmptyForm());

	const isEdit = Boolean(formId);

	const { data: formData, loading: formLoading } = useGetFormBuilderFormQuery({
		skip: !isEdit,
		variables: { id: Number(formId) },
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const { data: fieldTypesData } = useListFormBuilderItemFieldTypesQuery({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const { data: selectsData } = useListFormBuilderSelectsQuery({
		variables: { input: { pagination: { limit: 500, offset: 0 } } },
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const [createForm, { loading: creating }] = useCreateFormBuilderFormMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const [updateForm, { loading: updating }] = useUpdateFormBuilderFormMutation({
		context: { clientName: Constants.APOLLO_CLIENT_NUGGET_KMS_API_NAME },
	});

	const { exportResponses, loading: exportLoading } = useExportFormResponses(
		Number(formId ?? 0),
		formData?.getFormBuilderForm,
	);

	useEffect(() => {
		if (!formData?.getFormBuilderForm) {
			return;
		}
		const existing = formData.getFormBuilderForm;
		setFormState({
			code: existing.code ?? "",
			name: existing.name ?? "",
			description: existing.description ?? "",
			closingMessage: existing.closingMessage ?? "",
			logoUrl: existing.logoUrl ?? "",
			sections: existing.sections.map((section) => ({
				code: section.code ?? "",
				name: section.name ?? "",
				description: section.description ?? "",
				sortOrder: section.sortOrder,
				items: section.items.map((item) => ({
					code: item.code ?? "",
					name: item.name ?? "",
					description: item.description ?? "",
					sortOrder: item.sortOrder,
					relatedFormCode: item.relatedFormCode ?? "",
					relatedSectionCode: item.relatedSectionCode ?? "",
					relatedItemCode: item.relatedItemCode ?? "",
					relatedItemFieldCode: item.relatedItemFieldCode ?? "",
					itemFields: item.itemFields.map((field) => ({
						code: field.code ?? "",
						name: field.name ?? "",
						description: field.description ?? "",
						sortOrder: field.sortOrder,
						typeCode: field.typeCode ?? "",
						isRequired: field.isRequired === 1,
						selectId: field.selectId ?? null,
						placeholder: field.placeholder ?? "",
						subtitle: field.subtitle ?? "",
					})),
				})),
			})),
		});
	}, [formData]);

	const fieldTypes = useMemo(
		() => fieldTypesData?.listFormBuilderItemFieldTypes ?? [],
		[fieldTypesData],
	);
	const selects = useMemo(() => selectsData?.listFormBuilderSelects.rows ?? [], [selectsData]);

	const setFormField = (field: keyof FormBuilderFormState, value: string) => {
		setFormState((prev) => ({ ...prev, [field]: value }));
	};

	const updateSection = (sectionIndex: number, patch: Partial<FormBuilderSectionState>) => {
		setFormState((prev) => {
			const next = [...prev.sections];
			next[sectionIndex] = { ...next[sectionIndex], ...patch };
			return { ...prev, sections: next };
		});
	};

	const updateItem = (
		sectionIndex: number,
		itemIndex: number,
		patch: Partial<FormBuilderItemState>,
	) => {
		setFormState((prev) => {
			const nextSections = [...prev.sections];
			const nextItems = [...nextSections[sectionIndex].items];
			nextItems[itemIndex] = { ...nextItems[itemIndex], ...patch };
			nextSections[sectionIndex] = { ...nextSections[sectionIndex], items: nextItems };
			return { ...prev, sections: nextSections };
		});
	};

	const updateField = (
		sectionIndex: number,
		itemIndex: number,
		fieldIndex: number,
		patch: Partial<FormBuilderItemFieldState>,
	) => {
		setFormState((prev) => {
			const nextSections = [...prev.sections];
			const nextItems = [...nextSections[sectionIndex].items];
			const nextFields = [...nextItems[itemIndex].itemFields];
			nextFields[fieldIndex] = { ...nextFields[fieldIndex], ...patch };
			nextItems[itemIndex] = { ...nextItems[itemIndex], itemFields: nextFields };
			nextSections[sectionIndex] = { ...nextSections[sectionIndex], items: nextItems };
			return { ...prev, sections: nextSections };
		});
	};

	const addSection = () => {
		setFormState((prev) => ({
			...prev,
			sections: [...prev.sections, createEmptySection(prev.sections.length + 1)],
		}));
	};

	const removeSection = (sectionIndex: number) => {
		setFormState((prev) => ({
			...prev,
			sections: prev.sections.filter((_, index) => index !== sectionIndex),
		}));
	};

	const addItem = (sectionIndex: number) => {
		updateSection(sectionIndex, {
			items: [
				...formState.sections[sectionIndex].items,
				createEmptyItem(formState.sections[sectionIndex].items.length + 1),
			],
		});
	};

	const removeItem = (sectionIndex: number, itemIndex: number) => {
		updateSection(sectionIndex, {
			items: formState.sections[sectionIndex].items.filter((_, index) => index !== itemIndex),
		});
	};

	const addField = (sectionIndex: number, itemIndex: number) => {
		updateItem(sectionIndex, itemIndex, {
			itemFields: [
				...formState.sections[sectionIndex].items[itemIndex].itemFields,
				createEmptyField(formState.sections[sectionIndex].items[itemIndex].itemFields.length + 1),
			],
		});
	};

	const removeField = (sectionIndex: number, itemIndex: number, fieldIndex: number) => {
		updateItem(sectionIndex, itemIndex, {
			itemFields: formState.sections[sectionIndex].items[itemIndex].itemFields.filter(
				(_, index) => index !== fieldIndex,
			),
		});
	};

	const validate = () => {
		if (!formState.code.trim() || !formState.name.trim()) {
			toast.error("Form code and name are required.");
			return false;
		}

		const sectionCodeSet = new Set<string>();
		for (const section of formState.sections) {
			if (!section.name.trim() || !section.sortOrder) {
				toast.error("Section name and sort order are required.");
				return false;
			}
			if (section.code.trim()) {
				if (sectionCodeSet.has(section.code.trim())) {
					toast.error("Section code must be unique within the form.");
					return false;
				}
				sectionCodeSet.add(section.code.trim());
			}

			const itemCodeSet = new Set<string>();
			for (const item of section.items) {
				if (!item.name.trim() || !item.sortOrder) {
					toast.error("Item name and sort order are required.");
					return false;
				}
				if (item.code.trim()) {
					const scoped = `${section.sortOrder}-${item.code.trim()}`;
					if (itemCodeSet.has(scoped)) {
						toast.error("Item code must be unique within the section.");
						return false;
					}
					itemCodeSet.add(scoped);
				}

				const fieldCodeSet = new Set<string>();
				for (const field of item.itemFields) {
					if (!field.sortOrder || !field.typeCode.trim()) {
						toast.error("Item field sort order and type are required.");
						return false;
					}
					if (
						["select", "multiselect", "radio", "checkbox"].includes(field.typeCode) &&
						!field.selectId
					) {
						toast.error("Select fields must choose a select list.");
						return false;
					}
					if (field.code.trim()) {
						const scoped = `${item.sortOrder}-${field.code.trim()}`;
						if (fieldCodeSet.has(scoped)) {
							toast.error("Item field code must be unique within the item.");
							return false;
						}
						fieldCodeSet.add(scoped);
					}
				}
			}
		}

		return true;
	};

	const handleSave = async () => {
		if (!validate()) {
			return;
		}
		const input = {
			form: {
				code: formState.code.trim(),
				name: formState.name.trim(),
				description: formState.description.trim() || null,
				closingMessage: formState.closingMessage.trim() || null,
				logoUrl: formState.logoUrl.trim() || null,
			},
			sections: formState.sections.map((section) => ({
				code: section.code.trim() || null,
				name: section.name.trim(),
				description: section.description.trim() || null,
				sortOrder: section.sortOrder,
				items: section.items.map((item) => ({
					code: item.code.trim() || null,
					name: item.name.trim(),
					description: item.description.trim() || null,
					sortOrder: item.sortOrder,
					relatedFormCode: item.relatedFormCode.trim() || null,
					relatedSectionCode: item.relatedSectionCode.trim() || null,
					relatedItemCode: item.relatedItemCode.trim() || null,
					relatedItemFieldCode: item.relatedItemFieldCode.trim() || null,
					itemFields: item.itemFields.map((field) => ({
						code: field.code.trim() || null,
						name: field.name.trim() || null,
						description: field.description.trim() || null,
						sortOrder: field.sortOrder,
						typeCode: field.typeCode.trim(),
						isRequired: field.isRequired ? 1 : 0,
						selectId: field.selectId ?? null,
						placeholder: field.placeholder.trim() || null,
						subtitle: field.subtitle.trim() || null,
					})),
				})),
			})),
		};

		try {
			if (isEdit) {
				await updateForm({ variables: { id: Number(formId), input } });
				toast.success("Form updated.");
			} else {
				const res = await createForm({ variables: { input } });
				const createdId = res.data?.createFormBuilderForm.id;
				toast.success("Form created.");
				if (createdId) {
					history.replace(PathName.FORM_BUILDER_FORMS_EDIT(String(createdId)));
				}
			}
		} catch (error: any) {
			toast.error(error?.message || "Failed to save form.");
		}
	};

	if (formLoading && isEdit) {
		return <div className="formBuilder-card">Loading form...</div>;
	}

	return (
		<div className="formBuilder-edit">
			<div className="formBuilder-editHeader">
				<button type="button" onClick={() => history.push(PathName.FORM_BUILDER_FORMS)}>
					Back to forms
				</button>
				<div>
					<h2>{isEdit ? "Edit Form" : "Add Form"}</h2>
					<p>Define form structure and fields.</p>
				</div>
				<div style={{ display: "flex", gap: "0.5rem" }}>
					{isEdit && (
						<button
							type="button"
							className="formBuilder-button formBuilder-button--secondary"
							onClick={() => exportResponses()}
							disabled={exportLoading || !formData?.getFormBuilderForm?.sections?.length}
						>
							Export CSV&nbsp;
							<BsFiletypeCsv />
						</button>
					)}
					<button
						type="button"
						className="formBuilder-button formBuilder-button--primary"
						onClick={handleSave}
						disabled={creating || updating}
					>
						Save
					</button>
				</div>
			</div>

			<div className="formBuilder-card">
				<h3>Form Details</h3>
				<div className="formBuilder-grid">
					<label>
						Code
						<input
							type="text"
							value={formState.code}
							onChange={(event) => setFormField("code", event.target.value)}
						/>
					</label>
					<label>
						Name
						<input
							type="text"
							value={formState.name}
							onChange={(event) => setFormField("name", event.target.value)}
						/>
					</label>
					<label>
						Description
						<input
							type="text"
							value={formState.description}
							onChange={(event) => setFormField("description", event.target.value)}
						/>
					</label>
					<label>
						Closing Message
						<input
							type="text"
							value={formState.closingMessage}
							onChange={(event) => setFormField("closingMessage", event.target.value)}
						/>
					</label>
					<label>
						Logo URL
						<input
							type="text"
							value={formState.logoUrl}
							onChange={(event) => setFormField("logoUrl", event.target.value)}
						/>
					</label>
				</div>
			</div>

			{formState.sections.map((section, sectionIndex) => (
				<div className="formBuilder-card" key={`section-${sectionIndex}`}>
					<div className="formBuilder-cardHeader">
						<h3>Section {sectionIndex + 1}</h3>
						<button type="button" onClick={() => removeSection(sectionIndex)}>
							Remove Section
						</button>
					</div>
					<div className="formBuilder-grid">
						<label>
							Code
							<input
								type="text"
								value={section.code}
								onChange={(event) => updateSection(sectionIndex, { code: event.target.value })}
							/>
						</label>
						<label>
							Name
							<input
								type="text"
								value={section.name}
								onChange={(event) => updateSection(sectionIndex, { name: event.target.value })}
							/>
						</label>
						<label>
							Description
							<input
								type="text"
								value={section.description}
								onChange={(event) =>
									updateSection(sectionIndex, { description: event.target.value })
								}
							/>
						</label>
						<label>
							Sort Order
							<input
								type="number"
								value={section.sortOrder}
								onChange={(event) =>
									updateSection(sectionIndex, { sortOrder: toNumber(event.target.value) })
								}
							/>
						</label>
					</div>

					{section.items.map((item, itemIndex) => (
						<div className="formBuilder-subCard" key={`item-${itemIndex}`}>
							<div className="formBuilder-cardHeader">
								<h4>Item {itemIndex + 1}</h4>
								<button type="button" onClick={() => removeItem(sectionIndex, itemIndex)}>
									Remove Item
								</button>
							</div>
							<div className="formBuilder-grid">
								<label>
									Code
									<input
										type="text"
										value={item.code}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, { code: event.target.value })
										}
									/>
								</label>
								<label>
									Name
									<input
										type="text"
										value={item.name}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, { name: event.target.value })
										}
									/>
								</label>
								<label>
									Description
									<input
										type="text"
										value={item.description}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, { description: event.target.value })
										}
									/>
								</label>
								<label>
									Sort Order
									<input
										type="number"
										value={item.sortOrder}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, {
												sortOrder: toNumber(event.target.value),
											})
										}
									/>
								</label>
								<label>
									Related Form Code
									<input
										type="text"
										value={item.relatedFormCode}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, { relatedFormCode: event.target.value })
										}
									/>
								</label>
								<label>
									Related Section Code
									<input
										type="text"
										value={item.relatedSectionCode}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, {
												relatedSectionCode: event.target.value,
											})
										}
									/>
								</label>
								<label>
									Related Item Code
									<input
										type="text"
										value={item.relatedItemCode}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, { relatedItemCode: event.target.value })
										}
									/>
								</label>
								<label>
									Related Item Field Code
									<input
										type="text"
										value={item.relatedItemFieldCode}
										onChange={(event) =>
											updateItem(sectionIndex, itemIndex, {
												relatedItemFieldCode: event.target.value,
											})
										}
									/>
								</label>
							</div>

							{item.itemFields.map((field, fieldIndex) => (
								<div className="formBuilder-subCard" key={`field-${fieldIndex}`}>
									<div className="formBuilder-cardHeader">
										<h5>Field {fieldIndex + 1}</h5>
										<button
											type="button"
											onClick={() => removeField(sectionIndex, itemIndex, fieldIndex)}
										>
											Remove Field
										</button>
									</div>
									<div className="formBuilder-grid">
										<label>
											Code
											<input
												type="text"
												value={field.code}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														code: event.target.value,
													})
												}
											/>
										</label>
										<label>
											Name
											<input
												type="text"
												value={field.name}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														name: event.target.value,
													})
												}
											/>
										</label>
										<label>
											Description
											<input
												type="text"
												value={field.description}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														description: event.target.value,
													})
												}
											/>
										</label>
										<label>
											Sort Order
											<input
												type="number"
												value={field.sortOrder}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														sortOrder: toNumber(event.target.value),
													})
												}
											/>
										</label>
										<label>
											Type
											<select
												value={field.typeCode}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														typeCode: event.target.value,
													})
												}
											>
												<option value="">Select type</option>
												{fieldTypes.map((type) => (
													<option key={type.code} value={type.code}>
														{type.name}
													</option>
												))}
											</select>
										</label>
										<label>
											Required
											<input
												type="checkbox"
												checked={field.isRequired}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														isRequired: event.target.checked,
													})
												}
											/>
										</label>
										<label>
											Select List
											<select
												value={field.selectId ?? ""}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														selectId: event.target.value ? Number(event.target.value) : null,
													})
												}
											>
												<option value="">None</option>
												{selects.map((select) => (
													<option key={select.id} value={select.id}>
														{select.name}
													</option>
												))}
											</select>
										</label>
										<label>
											Placeholder
											<input
												type="text"
												value={field.placeholder}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														placeholder: event.target.value,
													})
												}
											/>
										</label>
										<label>
											Subtitle
											<input
												type="text"
												value={field.subtitle}
												onChange={(event) =>
													updateField(sectionIndex, itemIndex, fieldIndex, {
														subtitle: event.target.value,
													})
												}
											/>
										</label>
									</div>
								</div>
							))}

							<button
								type="button"
								className="formBuilder-button formBuilder-button--secondary"
								onClick={() => addField(sectionIndex, itemIndex)}
							>
								Add Field
							</button>
						</div>
					))}

					<button
						type="button"
						className="formBuilder-button formBuilder-button--secondary"
						onClick={() => addItem(sectionIndex)}
					>
						Add Item
					</button>
				</div>
			))}

			<button
				type="button"
				className="formBuilder-button formBuilder-button--secondary"
				onClick={addSection}
			>
				Add Section
			</button>
		</div>
	);
};
